import { useState, useEffect, useCallback } from 'react';
import { Users, Crown, CalendarDays, ChevronRight, ChevronLeft, Flame, Pencil, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../contexts/AuthContext';
import { getWeekStartTahiti, formatWeekRange, getTahitiDayOfWeek } from '../../utils/tahiti';

export function MyGroupWeekCard({ userId, userName }) {
  const isWeekend = getTahitiDayOfWeek() >= 5; // Sam/Dim Tahiti → semaine suivante par defaut
  const [weekOffset, setWeekOffset] = useState(isWeekend ? 1 : 0);
  const [groupData, setGroupData] = useState(null);
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [weekEstimate, setWeekEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingDay, setEditingDay] = useState(null);
  const [editValue, setEditValue] = useState('');

  const ws = getWeekStartTahiti(weekOffset);
  const isCurrentWeek = weekOffset === 0;

  const fetchWeekData = useCallback(async () => {
    setLoading(true);
    try {
      const [myGroupRes, groupRes, progRes] = await Promise.allSettled([
        apiClient.get(`/my-group?week_start=${ws}`),
        apiClient.get(`/groups/current?week_start=${ws}`),
        apiClient.get('/my-program'),
      ]);
      
      // Use dedicated /my-group endpoint (most reliable)
      let myGroup = null;
      if (myGroupRes.status === 'fulfilled' && myGroupRes.value.data?.found) {
        const mg = myGroupRes.value.data;
        myGroup = { group_num: mg.group_num, program: mg.program, group_name: mg.group_name, members: mg.members };
      }
      
      if (groupRes.status === 'fulfilled') {
        const gd = groupRes.value.data;
        // Fallback: if /my-group didn't work, try from /groups/current
        if (!myGroup && gd.my_group_num) {
          myGroup = (gd.groups || []).find(g => g.group_num === gd.my_group_num) || null;
        }
        setGroupData(myGroup ? { ...gd, myGroup } : { generated: gd.generated });
      } else {
        setGroupData(myGroup ? { myGroup, generated: true } : { generated: false });
      }
      
      if (progRes.status === 'fulfilled') {
        setWeeklyPlan(progRes.value.data?.weekly_plan || null);
      }
    } catch {} finally { setLoading(false); }

    if (isCurrentWeek) {
      apiClient.get('/training/week-estimate').then(r => setWeekEstimate(r.data)).catch(() => {});
    } else {
      setWeekEstimate(null);
    }
  }, [ws, userId, userName, isCurrentWeek]);

  useEffect(() => { fetchWeekData(); }, [fetchWeekData]);

  const handleSaveAdjust = async (dayIndex) => {
    const val = parseInt(editValue);
    if (isNaN(val) || val < 0) return;
    const todayIdx = new Date(Date.now() - 10 * 3600000).getUTCDay();
    const mappedToday = todayIdx === 0 ? 6 : todayIdx - 1;
    if (dayIndex === mappedToday && isCurrentWeek) {
      try {
        await apiClient.put('/training/today/adjust', { total_calories: val });
        setWeekEstimate(prev => ({
          ...prev,
          week: { ...prev.week, [dayIndex]: { ...prev.week[dayIndex], actual_calories: val, manual_override: true } }
        }));
      } catch {}
    }
    setEditingDay(null);
  };

  const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  // Cyclic muscle rotation based on group assignment (from /my-program weeklyPlan)
  const GROUP_MUSCLE_MAP = { 1: 'Dos', 2: 'Pectoraux', 3: 'Jambes', 4: 'Épaules', 5: 'Bras' };
  const todayMapped = getTahitiDayOfWeek(); // 0=Mon, 6=Sun

  const getDayMuscle = (i) => {
    // Use weekly plan from /my-program only for the current week (cyclic rotation per team)
    if (isCurrentWeek && weeklyPlan && i < 5) {
      const plan = weeklyPlan.find(p => p.day_of_week === i);
      if (plan) return plan.muscle_group;
    }
    // Fallback: compute cyclic rotation from group_num
    if (myGroup && myGroup.group_num && i < 5) {
      const startIdx = myGroup.group_num - 1;
      const gnum = ((startIdx + i) % 5) + 1;
      return GROUP_MUSCLE_MAP[gnum] || null;
    }
    return null;
  };

  const myGroup = groupData?.myGroup;

  return (
    <div className="space-y-3">
      {/* Week Navigator */}
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-3" data-testid="week-navigator">
        <div className="flex items-center justify-between">
          <button onClick={() => setWeekOffset(o => o - 1)} className="p-2 text-zinc-400 hover:text-white transition-colors" data-testid="week-prev">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="text-sm font-bold text-white" data-testid="week-range-label">{formatWeekRange(ws)}</p>
            <p className={`text-[10px] ${isWeekend && weekOffset === 1 ? 'text-[#FF6B35] font-semibold' : 'text-zinc-500'}`}>
              {weekOffset === 0 ? 'Semaine actuelle'
                : weekOffset === 1 ? (isWeekend ? 'Semaine prochaine — affichée auto (week-end)' : 'Semaine prochaine')
                : weekOffset === -1 ? 'Semaine derniere'
                : `${Math.abs(weekOffset)} sem. ${weekOffset > 0 ? 'apres' : 'avant'}`}
            </p>
          </div>
          <button onClick={() => setWeekOffset(o => o + 1)} className="p-2 text-zinc-400 hover:text-white transition-colors" data-testid="week-next">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        {weekOffset !== 0 && (
          <button onClick={() => setWeekOffset(0)} className="w-full mt-2 text-[10px] text-[#FF6B35] hover:underline" data-testid="week-today">
            Revenir a cette semaine
          </button>
        )}
      </div>

      {/* Mon Groupe */}
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4" data-testid="my-group-week-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            <Users className="w-5 h-5 text-[#FF6B35] inline mr-1 mb-0.5" /> Mon groupe
          </h2>
          <Link to="/entrainement" className="text-xs text-[#FF6B35] font-semibold">Voir <ChevronRight className="w-3 h-3 inline" /></Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : myGroup ? (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-meute flex items-center justify-center">
                <span className="font-mono text-xl font-bold text-white">{myGroup.group_num}</span>
              </div>
              <div>
                <p className="text-white font-bold">
                  {(() => {
                    const alpha = myGroup.members?.find(m => m.tier === 'alpha');
                    return alpha ? alpha.name : (myGroup.group_name || myGroup.program || `Groupe ${myGroup.group_num}`);
                  })()}
                </p>
                <p className="text-sm text-zinc-400"><span className="text-[#FF6B35]">{myGroup.program}</span> · {myGroup.members?.length} membres</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {myGroup.members?.map((m) => {
                const isMe = m.user_id === userId || (userName && m.name?.toLowerCase() === userName.toLowerCase());
                return (
                <span key={m.user_id} className={`px-2 py-1 rounded-md text-xs font-medium ${
                  isMe ? 'bg-[#FF6B35]/15 text-[#FF6B35]' :
                  m.tier === 'alpha' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-zinc-800 text-zinc-400'
                }`}>{m.name}{m.tier === 'alpha' && <Crown className="w-3 h-3 inline ml-0.5" />}</span>
                );
              })}
            </div>
          </>
        ) : groupData?.generated === false ? (
          <div className="flex items-center gap-3 p-3 bg-[#0F0F0F] rounded-lg">
            <CalendarDays className="w-10 h-10 text-zinc-500" />
            <div>
              <p className="text-sm text-zinc-300">Pas de groupes cette semaine</p>
              <p className="text-xs text-zinc-500">{weekOffset >= 0 ? 'Dispatch automatique samedi 22h' : 'Semaine passee sans dispatch'}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 p-3 bg-[#0F0F0F] rounded-lg">
            <CalendarDays className="w-10 h-10 text-zinc-500" />
            <div>
              <p className="text-sm text-zinc-300">Tu n&#39;es pas dans un groupe cette semaine</p>
            </div>
          </div>
        )}
      </div>

      {/* Planning Semaine */}
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4" data-testid="week-planning">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            <CalendarDays className="w-5 h-5 text-[#FF6B35] inline mr-1 mb-0.5" /> Planning semaine
          </h2>
          <Link to="/entrainement?tab=planning" className="text-xs text-[#FF6B35] font-semibold">Editer <ChevronRight className="w-3 h-3 inline" /></Link>
        </div>

        {weekEstimate?.profile && isCurrentWeek && (
          <div className="flex items-center gap-2 mb-2 text-[9px] text-zinc-600">
            <span>Calcul auto · {weekEstimate.profile.weight}kg · {weekEstimate.profile.gender === 'female' ? 'F' : 'H'} · {weekEstimate.profile.age}ans</span>
          </div>
        )}

        <div className="space-y-1.5">
          {DAYS.map((dayName, i) => {
            const isToday = i === todayMapped && isCurrentWeek;
            const muscle = getDayMuscle(i);
            const est = weekEstimate?.week?.[i];
            const calories = est?.actual_calories ?? est?.estimated_calories ?? 0;
            const isManual = est?.manual_override;
            const isEditing = editingDay === i;

            return (
              <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-md ${isToday ? 'bg-[#FF6B35]/10 border border-[#FF6B35]/20' : 'bg-[#0F0F0F]'}`}>
                <span className={`text-xs font-bold w-8 ${isToday ? 'text-[#FF6B35]' : 'text-zinc-500'}`}>{dayName.substring(0, 3)}</span>
                <div className="flex-1 min-w-0">
                  {muscle ? (
                    <p className="text-sm text-white truncate">{muscle}</p>
                  ) : (
                    <p className="text-sm text-zinc-500">Repos</p>
                  )}
                </div>

                {calories > 0 && !isEditing && isCurrentWeek && (
                  <div className="flex items-center gap-1">
                    <Flame className="w-3 h-3 text-[#FFB800]" />
                    <span className={`font-mono text-xs font-bold ${isManual ? 'text-[#FFB800]' : 'text-zinc-400'}`} data-testid={`cal-day-${i}`}>
                      ~{calories}
                    </span>
                    <span className="text-[8px] text-zinc-600">kcal</span>
                    {isToday && (
                      <button onClick={() => { setEditingDay(i); setEditValue(String(calories)); }}
                        className="ml-1 text-zinc-600 hover:text-[#FF6B35] p-0.5" data-testid={`edit-cal-${i}`}>
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}

                {isEditing && (
                  <div className="flex items-center gap-1">
                    <input
                      type="number" value={editValue} onChange={e => setEditValue(e.target.value)}
                      className="w-16 bg-[#0F0F0F] border border-[#FF6B35]/50 rounded text-xs text-white font-mono px-1.5 py-0.5 text-right"
                      autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveAdjust(i); if (e.key === 'Escape') setEditingDay(null); }}
                      data-testid={`cal-input-${i}`}
                    />
                    <button onClick={() => handleSaveAdjust(i)} className="text-green-400 hover:text-green-300 p-0.5" data-testid={`cal-save-${i}`}>
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {isToday && !isEditing && <span className="text-[10px] text-[#FF6B35] font-bold px-2 py-0.5 bg-[#FF6B35]/15 rounded-full">Auj.</span>}
              </div>
            );
          })}
        </div>

        {weekEstimate && isCurrentWeek && (
          <div className="mt-3 flex items-center justify-between px-1">
            <span className="text-[10px] text-zinc-600">~calories brulees / semaine</span>
            <span className="font-mono text-sm font-bold text-[#FFB800]" data-testid="week-total-cal">
              {Object.values(weekEstimate.week || {}).reduce((sum, d) => sum + (d.actual_calories ?? d.estimated_calories ?? 0), 0)} kcal
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
